import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { orpc } from "@/lib/orpc";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { quizOf, slidesOf } from "../-components/course-content";
import { QuizForm, type QuizResult } from "../-components/quiz-form";
import { QuizOutcome } from "../-components/quiz-outcome";
import { SlidePanel } from "../-components/slide-panel";

export const Route = createFileRoute("/_authenticated/_workforce/training/play/$assignmentId")({
  component: TakeCourse,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** Working through a course: the material, then the quiz, then the verdict. */
function TakeCourse() {
  const { assignmentId } = Route.useParams();
  const queryClient = useQueryClient();
  const [slide, setSlide] = useState<number | null>(null);
  const [onQuiz, setOnQuiz] = useState(false);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<QuizResult | null>(null);

  const assignment = useQuery(
    orpc.workforce.training.getAssignment.queryOptions({ input: { assignmentId } }),
  );

  const progress = useMutation(
    orpc.workforce.training.recordProgress.mutationOptions({ onError: fail }),
  );
  const submit = useMutation(
    orpc.workforce.training.submit.mutationOptions({
      onSuccess: async (outcome) => {
        setResult(outcome);
        toast[outcome.passed ? "success" : "error"](
          outcome.passed ? "Passed" : "Not passed this time",
        );
        await invalidateKeys(queryClient, [
          orpc.workforce.key(),
          orpc.dashboard.workforce.key(),
          orpc.administration.staff.key(),
        ]);
      },
      onError: fail,
    }),
  );

  if (assignment.isPending) return <Skeleton className="h-64 w-full" />;
  if (assignment.isError || !assignment.data) {
    return (
      <EmptyState
        title="Could not open this course"
        description="Your progress is saved. Try again in a moment."
        action={<Button onClick={() => assignment.refetch()}>Try again</Button>}
      />
    );
  }

  const course = assignment.data.course;
  const slides = slidesOf(course.slides);
  const quiz = quizOf(course.quiz);
  /* Somebody who stopped halfway through a twenty-slide course came back to
     slide one: their place was on the record and nothing read it. */
  const at = slide ?? Math.min(assignment.data.currentSlide ?? 0, Math.max(slides.length - 1, 0));
  const onLastSlide = at >= slides.length - 1;
  const attempt = (assignment.data.quizAttempts ?? 0) + 1;
  const answered = quiz.questions.filter((question) => answers[question.id] !== undefined).length;

  if (result) {
    return (
      <QuizOutcome
        result={result}
        questions={quiz.questions}
        answers={answers}
        passMark={course.passingScore}
        onRetry={() => {
          setResult(null);
          setAnswers({});
          setOnQuiz(true);
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={course.title}
        actions={
          <>
            <Badge variant="outline">
              {onQuiz
                ? `Quiz, attempt ${attempt}${course.maxAttempts ? ` of ${course.maxAttempts}` : ""}`
                : slides.length > 0
                  ? `Section ${at + 1} of ${slides.length}`
                  : "No material"}
            </Badge>
            <Link to="/training" className="text-muted-foreground text-sm hover:underline">
              My training
            </Link>
          </>
        }
      />

      {onQuiz ? (
        <QuizForm
          questions={quiz.questions}
          answers={answers}
          answered={answered}
          passMark={course.passingScore}
          attempt={attempt}
          maxAttempts={course.maxAttempts}
          pending={submit.isPending}
          onAnswer={(questionId, option) =>
            setAnswers((previous) => ({ ...previous, [questionId]: option }))
          }
          onBack={slides.length > 0 ? () => setOnQuiz(false) : undefined}
          onSubmit={() => submit.mutate({ assignmentId, answers })}
        />
      ) : (
        <>
          <SlidePanel slide={slides[at]} />
          {slides[at]?.durationMinutes ? (
            <p className="text-muted-foreground text-sm">
              About {slides[at]?.durationMinutes} minutes
            </p>
          ) : null}

          {slides.length > 0 ? (
            <div className="flex gap-2">
              <Button variant="outline" disabled={at === 0} onClick={() => setSlide(at - 1)}>
                Back
              </Button>
              <Button
                disabled={onLastSlide}
                onClick={() => {
                  const next = Math.min(slides.length - 1, at + 1);
                  setSlide(next);
                  progress.mutate({ assignmentId, currentSlide: next, slidesCompleted: next });
                }}
              >
                Next
              </Button>
            </div>
          ) : null}

          {onLastSlide || slides.length === 0 ? (
            <Card>
              <CardContent className="space-y-3">
                {quiz.questions.length > 0 ? (
                  <>
                    <p className="font-medium">Ready for the quiz</p>
                    <p className="text-muted-foreground text-sm">
                      {quiz.questions.length}{" "}
                      {quiz.questions.length === 1 ? "question" : "questions"},{" "}
                      {course.passingScore}% to pass, attempt {attempt}
                      {course.maxAttempts ? ` of ${course.maxAttempts}` : ""}.
                    </p>
                    <Button onClick={() => setOnQuiz(true)}>Start the quiz</Button>
                  </>
                ) : (
                  <>
                    <p className="font-medium">That is the end of the material</p>
                    <Button
                      disabled={submit.isPending}
                      onClick={() => submit.mutate({ assignmentId, answers: {} })}
                    >
                      Mark complete
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}
